"use client";

import ApplicationJobHeader from "@/components/careers/ApplicationJobHeader";
import ApplicationProgress from "@/components/careers/ApplicationProgress";
import Button from "@/components/library/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  APPLICANT_DOCUMENT_MAX_BYTES,
  APPLICANT_DOCUMENT_MAX_COUNT,
  applicantJobQueryString,
} from "@/utils/jobApplicantsPublic";
import type { JobListing } from "@/utils/jobsPublic";
import type { SupportedLanguage } from "@/utils/library/languages";
import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type JobApplicationDialogProps = {
  job: JobListing | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBackToJob?: () => void;
  locale: SupportedLanguage;
  sourceUrl: string;
};

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  linkedinUrl: string;
  portfolioUrl: string;
  mostRecentEmployer: string;
  teachUs: string;
};

const initialForm: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  linkedinUrl: "",
  portfolioUrl: "",
  mostRecentEmployer: "",
  teachUs: "",
};

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");
}

export default function JobApplicationDialog({
  job,
  open,
  onOpenChange,
  onBackToJob,
  locale,
  sourceUrl,
}: Readonly<JobApplicationDialogProps>) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState<FormState>(initialForm);
  const [files, setFiles] = useState<File[]>([]);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [phase, setPhase] = useState<"form" | "progress" | "success">("form");
  const [progressMessage, setProgressMessage] = useState("");
  const [error, setError] = useState("");

  const reset = useCallback(() => {
    setForm(initialForm);
    setFiles([]);
    setIsDraggingFiles(false);
    setPhase("form");
    setProgressMessage("");
    setError("");
  }, []);

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleCancel = () => {
    reset();
    if (onBackToJob) {
      onBackToJob();
      return;
    }
    onOpenChange(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateSelectedFiles = (selected: File[]) => {
    const valid: File[] = [];
    for (const file of selected) {
      if (file.size > APPLICANT_DOCUMENT_MAX_BYTES) {
        setError(t("careers.application.errors.fileTooLarge"));
        return;
      }
      valid.push(file);
    }
    setError("");
    setFiles(valid.slice(0, APPLICANT_DOCUMENT_MAX_COUNT));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSelectedFiles(Array.from(e.target.files ?? []));
    e.target.value = "";
  };

  const handleFileDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingFiles(true);
  };

  const handleFileDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingFiles(false);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingFiles(false);
    updateSelectedFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const isFormValid = Boolean(
    form.firstName.trim() &&
      form.lastName.trim() &&
      form.email.trim() &&
      form.phone.trim() &&
      form.linkedinUrl.trim() &&
      form.mostRecentEmployer.trim() &&
      form.teachUs.trim() &&
      files.length > 0,
  );

  const uploadFiles = async (
    applicantId: string,
    jobQuery: string,
  ): Promise<boolean> => {
    for (const [index, file] of files.entries()) {
      setProgressMessage(
        t("careers.application.progress.uploadingFile", {
          filename: file.name,
          current: index + 1,
          total: files.length,
        }),
      );

      const body = new FormData();
      body.append("file", file);

      const uploadRes = await fetch(
        `/api/jobs/applicants/${applicantId}/documents?${jobQuery}`,
        { method: "POST", body },
      );

      if (uploadRes.status === 429) {
        setError(t("careers.application.errors.rateLimit"));
        setPhase("form");
        return false;
      }

      if (!uploadRes.ok) {
        const data = (await uploadRes.json().catch(() => ({}))) as {
          error?: string;
        };
        if (data.error?.includes("4 MB")) {
          setError(t("careers.application.errors.fileTooLarge"));
        } else if (uploadRes.status === 400 || uploadRes.status === 422) {
          setError(t("careers.application.errors.fileUpload"));
        } else {
          setError(t("careers.application.errors.generic"));
        }
        setPhase("form");
        return false;
      }
    }

    return true;
  };

  const submit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!job || !isFormValid) {
      const validationError =
        files.length === 0
          ? t("careers.application.errors.resumeRequired")
          : t("careers.application.errors.validation");
      setError(validationError);
      return;
    }

    setError("");
    setPhase("progress");
    setProgressMessage(t("careers.application.progress.savingInfo"));

    try {
      const createRes = await fetch("/api/jobs/applicants", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          locale,
          sourceUrl,
          marketingId: getCookie("marketing_id"),
          job: { id: job.id, type: job.type, title: job.title },
          applicant: {
            ...form,
            portfolioUrl: form.portfolioUrl.trim() || undefined,
          },
        }),
      });

      if (!createRes.ok) {
        throw new Error("create_failed");
      }

      const { applicantId } = (await createRes.json()) as {
        applicantId: string;
      };
      const jobQuery = applicantJobQueryString({
        type: job.type,
        id: job.id,
      });

      const uploaded = await uploadFiles(applicantId, jobQuery);
      if (!uploaded) return;

      setProgressMessage(t("careers.application.progress.finishing"));

      const completeRes = await fetch(
        `/api/jobs/applicants/${applicantId}/complete?${jobQuery}`,
        { method: "POST" },
      );

      if (!completeRes.ok) {
        throw new Error("complete_failed");
      }

      setPhase("success");
    } catch {
      setError(t("careers.application.errors.generic"));
      setPhase("form");
    }
  };

  if (!job) return null;

  const inputClass =
    "w-full rounded-md border border-gail-silver px-4 py-3 text-foreground focus:outline-none focus:ring-1 focus:ring-gail-primary";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        size="lg"
        className={clsx(
          // The whole dialog scrolls as one column — no pinned header/footer.
          "flex flex-col overflow-y-auto overscroll-contain p-0",
          // Full-screen sheet on mobile so the form is usable and scrollable.
          "top-0 left-0 h-dvh max-h-dvh w-full max-w-none translate-x-0 translate-y-0",
          // Centered card on sm and up.
          "sm:top-[50%] sm:left-[50%] sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:translate-x-[-50%] sm:translate-y-[-50%]",
        )}
      >
        {phase === "success" ? (
          <div className="my-auto px-6 py-8 text-center sm:px-8">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gail-primary-light font-monument text-xl text-gail-primary">
              ✓
            </div>
            <h3 className="font-monument text-2xl font-medium">
              {t("careers.application.success.title")}
            </h3>
            <p className="mx-auto mt-3 max-w-md text-gail-gray">
              {t("careers.application.success.body")}
            </p>
            <div className="mt-8">
              <Button type="primary" onClick={() => handleClose(false)}>
                {t("careers.application.backToJob")}
              </Button>
            </div>
          </div>
        ) : null}
        {phase === "progress" ? (
          <div className="flex flex-1 flex-col">
            <ApplicationJobHeader job={job} />
            <div className="flex-1 px-6 py-6 sm:px-8">
              <ApplicationProgress message={progressMessage} />
            </div>
          </div>
        ) : null}
        {phase === "form" ? (
          <>
            <ApplicationJobHeader job={job} />

            <div className="flex-1 px-6 py-6 sm:px-8">
              <DialogHeader className="text-left">
                <DialogTitle className="font-monument text-2xl font-medium">
                  {t("careers.application.title", { jobTitle: job.title })}
                </DialogTitle>
                <DialogDescription className="mb-10 text-base text-gail-gray">
                  {t("careers.application.subtitle")}
                </DialogDescription>
              </DialogHeader>

              <form
                ref={formRef}
                id="job-application-form"
                className="space-y-6"
                onSubmit={submit}
              >
              {error ? (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block text-sm font-medium">
                  {t("careers.application.firstName")}{" "}
                  <span>{t("careers.application.requiredMark")}</span>
                  <input
                    required
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    className={clsx("mt-1", inputClass)}
                  />
                </label>
                <label className="block text-sm font-medium">
                  {t("careers.application.lastName")}{" "}
                  <span>{t("careers.application.requiredMark")}</span>
                  <input
                    required
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    className={clsx("mt-1", inputClass)}
                  />
                </label>
                <label className="block text-sm font-medium">
                  {t("careers.application.email")}{" "}
                  <span>{t("careers.application.requiredMark")}</span>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className={clsx("mt-1", inputClass)}
                  />
                </label>
                <label className="block text-sm font-medium">
                  {t("careers.application.phone")}{" "}
                  <span>{t("careers.application.requiredMark")}</span>
                  <input
                    required
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder={t("careers.application.placeholders.phone")}
                    className={clsx("mt-1", inputClass)}
                  />
                </label>
                <label className="block text-sm font-medium">
                  {t("careers.application.linkedinUrl")}{" "}
                  <span>{t("careers.application.requiredMark")}</span>
                  <input
                    required
                    type="url"
                    name="linkedinUrl"
                    value={form.linkedinUrl}
                    onChange={handleChange}
                    placeholder={t(
                      "careers.application.placeholders.linkedinUrl",
                    )}
                    className={clsx("mt-1", inputClass)}
                  />
                </label>
                <label className="block text-sm font-medium">
                  {t("careers.application.portfolioUrl")}
                  <input
                    type="url"
                    name="portfolioUrl"
                    value={form.portfolioUrl}
                    onChange={handleChange}
                    placeholder={t(
                      "careers.application.placeholders.portfolioUrl",
                    )}
                    className={clsx("mt-1", inputClass)}
                  />
                </label>
              </div>

              <label className="block text-sm font-medium">
                {t("careers.application.mostRecentEmployer")}{" "}
                <span>{t("careers.application.requiredMark")}</span>
                <input
                  required
                  name="mostRecentEmployer"
                  value={form.mostRecentEmployer}
                  onChange={handleChange}
                  placeholder={t("careers.application.placeholders.employer")}
                  className={clsx("mt-1", inputClass)}
                />
              </label>

              <label className="block text-sm font-medium">
                {t("careers.application.teachUs")}{" "}
                <span>{t("careers.application.requiredMark")}</span>
                <textarea
                  required
                  name="teachUs"
                  rows={4}
                  value={form.teachUs}
                  onChange={handleChange}
                  placeholder={t("careers.application.placeholders.teachUs")}
                  className={clsx("mt-1 resize-y", inputClass)}
                />
              </label>

              <div>
                <p className="text-sm font-medium">
                  {t("careers.application.resumeOptional")}
                  <span> {t("careers.application.requiredMark")}</span>
                </p>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- drag-and-drop target; activation via nested file input */}
                <div
                  className={clsx(
                    "mt-2 rounded-xl border-2 border-dashed border-gail-silver text-center transition-colors hover:border-gail-primary hover:bg-gail-primary-light/30",
                    isDraggingFiles &&
                      "border-gail-primary bg-gail-primary-light/30",
                  )}
                  onDragOver={handleFileDragOver}
                  onDragLeave={handleFileDragLeave}
                  onDrop={handleFileDrop}
                >
                  <label className="flex w-full cursor-pointer flex-col items-center justify-center px-6 py-10">
                  <span className="font-monument text-base font-medium">
                    {t("careers.application.uploadResume")}
                  </span>
                  <span className="mt-1 text-sm text-gail-gray">
                    {t("careers.application.uploadFormats")}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    multiple
                    className="sr-only"
                    onChange={handleFileChange}
                    disabled={files.length >= APPLICANT_DOCUMENT_MAX_COUNT}
                  />
                  </label>
                </div>
                {files.length > 0 ? (
                  <ul className="mt-3 space-y-2 text-sm text-gail-gray">
                    {files.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          className="cursor-pointer text-gail-primary hover:underline"
                          onClick={() => removeFile(index)}
                        >
                          {t("careers.application.removeFile")}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              </form>
            </div>

            <DialogFooter className="flex-row justify-between gap-3 border-t border-gail-silver/30 bg-white px-6 py-4 sm:px-8">
              <Button type="tertiary" onClick={handleCancel}>
                {t("careers.application.backToJob")}
              </Button>
              <Button
                type="primary"
                htmlType="button"
                disabled={!isFormValid}
                onClick={() => formRef.current?.requestSubmit()}
              >
                {t("careers.application.submit")}
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
